import { useId } from 'react';

import Text from '@cartwhl/ui/base/TextNew';

import { publicPageGutters } from '../public-page-gutters';
import IntegrationRibbon from './IntegrationRibbon';
import type { ProductSpec, ProductSpecifications } from './types';

interface SpecDetailProps {
  detail: ProductSpec['detail'];
}

function SpecDetail({ detail }: SpecDetailProps) {
  if (detail.type === 'models') {
    return (
      <div className="flex max-w-[603px] flex-col gap-8">
        {detail.items.map((model) => (
          <Text
            as="p"
            key={model.name}
            token={{ base: '16', sm: '20' }}
            leading="editorial"
            className="text-secondary"
          >
            <span className="text-primary cw-green-underline">
              {model.name}
            </span>
            {` - ${model.description}`}
          </Text>
        ))}
      </div>
    );
  }

  if (detail.type === 'formats') {
    return (
      <>
        {/* Figma uses a continuous line box on mobile and trimmed, spaced items
            on desktop. Only one representation is exposed at each width. */}
        <Text
          as="p"
          token="16"
          leading="editorial"
          className="text-secondary whitespace-pre-line sm:hidden"
        >
          {detail.items.join('\n')}
        </Text>
        <ul className="hidden list-none flex-col gap-4 p-0 sm:flex">
          {detail.items.map((format) => (
            <li key={format}>
              <Text
                as="p"
                token="20"
                leading="editorial"
                className="text-secondary"
              >
                {format}
              </Text>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <Text
      as="p"
      token={{ base: '16', sm: '20' }}
      leading="editorial"
      className="text-secondary whitespace-pre-line"
    >
      {detail.text}
    </Text>
  );
}

interface ProductSpecsProps {
  specs: ProductSpecifications;
}

export default function ProductSpecs({ specs }: ProductSpecsProps) {
  const headingId = useId();

  return (
    <section
      className={`border-primary border-b py-10 sm:py-20 ${publicPageGutters}`}
      data-product-specs
    >
      <header className="flex flex-col gap-10">
        <div>
          <Text
            as="h2"
            id={headingId}
            token={{ base: '24/medium', lg: '32/medium' }}
            leading="editorial"
          >
            {specs.title}
          </Text>
        </div>
        <Text
          as="p"
          token="24"
          leading="editorial"
          className="text-secondary max-w-[648px] text-pretty"
        >
          {specs.description}
        </Text>
      </header>
      <div className="border-primary -mx-4 mt-10 pb-10 sm:mx-0 sm:rounded-2xl sm:border sm:pb-0">
        <table
          aria-labelledby={headingId}
          className="w-full table-fixed border-separate border-spacing-0 text-left"
        >
          <colgroup>
            <col className="w-[36.3%] sm:w-[184px] lg:w-[366px] xl:w-[584px]" />
            <col />
          </colgroup>
          <thead>
            <tr className="bg-secondary sm:bg-transparent">
              {specs.columns.map((column, index) => (
                <th
                  key={column}
                  scope="col"
                  className={`border-primary py-[22px] pr-4 sm:border-b sm:px-8 sm:pb-8 sm:pt-[50px] ${index === 0 ? 'pl-4' : 'pl-0'}`}
                >
                  <Text as="p" token="16" leading="body" className="sm:hidden">
                    {column}
                  </Text>
                  <div className="hidden min-h-6 sm:block">
                    <Text
                      as="p"
                      token={{ base: '20', lg: '24' }}
                      leading="editorial"
                      className="tracking-tight"
                    >
                      {column}
                    </Text>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {specs.rows.map((spec) => (
              <tr key={spec.id} className="group" data-spec-row={spec.id}>
                <th
                  scope="row"
                  className="after:bg-gray-30 relative break-words border-b border-transparent py-8 pl-4 pr-4 align-top after:absolute after:inset-x-0 after:bottom-0 after:left-4 after:h-px group-first:pt-7 sm:pl-8 sm:pr-14 sm:after:left-0 sm:group-first:pt-8 sm:group-last:border-b-0 sm:group-last:pb-10 sm:group-last:after:hidden"
                >
                  <Text
                    as="p"
                    token="16"
                    leading="body"
                    className="text-secondary sm:hidden"
                  >
                    {spec.label}
                  </Text>
                  <Text
                    as="p"
                    token="20"
                    leading="editorial"
                    className="text-secondary hidden sm:block"
                  >
                    {spec.label}
                  </Text>
                </th>
                <td className="after:bg-gray-30 relative break-words border-b border-transparent py-8 pl-0 pr-4 align-top after:absolute after:inset-x-0 after:bottom-0 after:right-4 after:h-px group-first:pt-7 sm:px-8 sm:after:right-0 sm:group-first:pt-8 sm:group-last:border-b-0 sm:group-last:pb-10 sm:group-last:after:hidden">
                  <SpecDetail detail={spec.detail} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="-mx-4 mt-10 sm:mx-0">
        <IntegrationRibbon />
      </div>
    </section>
  );
}
